import type { RawAxiosRequestConfig } from 'axios';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import { fsumClient } from '@/services/FsumHttpClient';
import type {
    FlightSchoolResponse,
    KnowledgeTestAgreementResponse,
} from '@/types/Response/FlightSchoolResponse';
import type { FaaTestReport } from '@/types/FaaTestReport';
import { formatObjectToFormData } from '@/functions/formatObjectToFormData';
import type { FaaTestReportResponse } from '@/types/Response/FaaTestReportResponse';
import { formatDate, formatDateTime } from '@/functions/formatDate';
import type { PassRateReport } from '@/types/PassRateReport';

/**
 * Retrieve list of Flight Schools available to the admin.
 * @todo: use the customer id.
 * @param cusId - ID of current admin.
 * @returns Promise<FlightSchool[]>
 */
// eslint-disable-next-line
async function getFlightSchools(cusId: number): Promise<FlightSchoolResponse[]> {
    return fsumClient.get('/v1/flight-schools');
}

async function getAllFlightSchools(): Promise<FlightSchoolResponse[]> {
    return fsumClient.get('/v1/flight-schools');
}

async function getSchoolCourses(
    school: FlightSchool,
): Promise<FlightSchoolCourse[]> {
    return fsumClient.get(`/v2/flight-schools/${school.id}/courses`);
}

/**
 * Retrieve all students that are enrolled in the flight school and are taking the course.
 */
async function getCourseStudents(
    school: FlightSchool,
    course: FlightSchoolCourse,
): Promise<FlightSchoolCourseStudent[]> {
    return fsumClient.get(`/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students`);
}

async function getStudent(
    school: FlightSchool,
    course: FlightSchoolCourse,
    studentId: number,
    ratingUuid: string,
): Promise<FlightSchoolCourseStudent> {
    return fsumClient.get(`/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${studentId}/ratings/${ratingUuid}`);
}

async function getStudentKnowledgeTestAgreement(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
): Promise<KnowledgeTestAgreementResponse> {
    return fsumClient.get(`/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/knowledge-test-agreement`);
}

async function getStudentEnrollmentCertificate(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
): Promise<Blob> {
    const { part, uuid } = course.certificate;
    const config: RawAxiosRequestConfig = {
        responseType: 'blob',
        headers: {
            Accept: 'application/pdf',
        },
    };

    return fsumClient.post(
        `/v1/flight-schools/${school.id}/courses/${uuid}/enrollment-certificate`,
        { part, studentId: student.id, rating: student.ratingUuid },
        config,
    );
}

async function getStudentGraduationCertificate(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
): Promise<Blob> {
    const { part, uuid } = course.certificate;
    const config: RawAxiosRequestConfig = {
        responseType: 'blob',
        headers: {
            Accept: 'application/pdf',
        },
    };

    return fsumClient.post(
        `/v1/flight-schools/${school.id}/courses/${uuid}/graduation-certificate`,
        { part, studentId: student.id, rating: student.ratingUuid },
        config,
    );
}

async function createSchool(
    flightSchool: Omit<FlightSchool, 'id' | 'courses' | 'owner'>,
    courseAcronyms: Array<string>,
    certificateUuids: Array<string>,
    email: string,
): Promise<FlightSchoolResponse> {
    return fsumClient.post(
        '/v1/flight-schools',
        {
            flightSchool,
            courseAcronyms,
            certificateUuids,
            email,
        },
    );
}

async function updateSchool(
    flightSchool: Omit<FlightSchool, 'courses' | 'owner'>,
    courseAcronyms: Array<string>,
    certificateUuids: Array<string>,
    email: string,
): Promise<FlightSchoolResponse> {
    return fsumClient.put(
        `/v1/flight-schools/${flightSchool.id}`,
        {
            flightSchool,
            courseAcronyms,
            certificateUuids,
            email,
        },
    );
}

async function deleteSchool(id: number): Promise<void> {
    return fsumClient.delete(`/v1/flight-schools/${id}`);
}

async function lockStudentCourseAccess(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
): Promise<void> {
    const route = `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/user-lockouts/lock`;
    return fsumClient.post(route);
}

async function unlockStudentCourseAccess(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
): Promise<void> {
    const route = `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/user-lockouts/unlock`;
    return fsumClient.post(route);
}

async function getFaaTestReports(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
): Promise<FaaTestReportResponse[]> {
    const route = `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/faa-test-reports`;
    const reports = fsumClient.get<FaaTestReportResponse[]>(route);

    const { locale } = new Intl.DateTimeFormat().resolvedOptions();

    return reports.then(
        (value: FaaTestReportResponse[]) => value.map<FaaTestReportResponse>(
            (report) => ({
                ...report,
                examDate: formatDate(report.examDate, locale),
                createdAt: formatDateTime(report.createdAt),
            }),
        ),
    );
}

async function createFaaTestReport(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
    report: FaaTestReport,
): Promise<FaaTestReportResponse> {
    const route = `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/faa-test-reports`;
    const formData = formatObjectToFormData(report);

    const config: RawAxiosRequestConfig = {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    };

    return fsumClient.post(route, formData, config);
}

async function updateFaaTestReport(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
    report: FaaTestReport,
    id: number,
): Promise<FaaTestReportResponse> {
    const route = `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/faa-test-reports/${id}`;
    const formData = formatObjectToFormData(report);

    const config: RawAxiosRequestConfig = {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    };

    return fsumClient.put(route, formData, config);
}

async function deleteFaaTestReport(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
    id: number,
): Promise<void> {
    const route = `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/faa-test-reports/${id}`;
    return fsumClient.delete(route);
}

async function getFaaTestReportFile(
    school: FlightSchool,
    course: FlightSchoolCourse,
    student: FlightSchoolCourseStudent,
    fileName: string,
): Promise<Blob> {
    const config: RawAxiosRequestConfig = {
        responseType: 'blob',
        headers: {
            Accept: 'application/pdf,image/jpeg,image/png,image/gif,image/heic,image/heif',
        },
    };
    return fsumClient.get(
        `/v1/flight-schools/${school.id}/courses/${course.certificate.uuid}/students/${student.id}/ratings/${student.ratingUuid}/faa-test-reports/${fileName}`,
        config,
    );
}

async function changeStudentFlightSchool(
    student: FlightSchoolCourseStudent,
    newSchool: FlightSchool,
): Promise<FlightSchoolCourseStudent> {
    const route = `/v1/students/${student.id}/ratings/${student.ratingUuid}/school`;
    return fsumClient.patch(route, { flightSchoolId: newSchool.id });
}

async function getPassRateReport(school: FlightSchool, startDate?: string, endDate?: string): Promise<PassRateReport> {
    const config: RawAxiosRequestConfig = {
        params: {
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
        },
    };

    const promise: Promise<PassRateReport> = fsumClient.get(`/v2/flight-schools/${school.id}/reports/pass-rate`, config);

    return promise.then(
        (value: PassRateReport) => {
            const passRateReport = value;

            passRateReport.courseRates = passRateReport.courseRates.sort(
                (a, b) => (a.course.certificate.title > b.course.certificate.title ? -1 : 1),
            );

            return passRateReport;
        },
    );
}

export default {
    getFlightSchools,
    getAllFlightSchools,
    getSchoolCourses,
    getCourseStudents,
    getStudent,
    getStudentKnowledgeTestAgreement,
    getStudentEnrollmentCertificate,
    getStudentGraduationCertificate,
    createSchool,
    updateSchool,
    deleteSchool,
    lockStudentCourseAccess,
    unlockStudentCourseAccess,
    getFaaTestReports,
    createFaaTestReport,
    updateFaaTestReport,
    deleteFaaTestReport,
    getFaaTestReportFile,
    changeStudentFlightSchool,
    getPassRateReport,
};
